import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import "./DesktopDashboard.css";
import AccountInfo from "./AccountInfo";
import AddAppWizard from "./AddAppWizard";
import WithdrawPanel from "./WithdrawPanel";
import { appsApi, withdrawApi, statsApi, contactApi, authApi } from "../../../api";

const navItems = [
  { id: "stats", label: "Statistics", icon: "📈" },
  { id: "billing", label: "Billing", icon: "💳" },
  // { id: "offers", label: "Offers Gallery", icon: "🎨" },
  { id: "placements", label: "App Placements", icon: "📱" },
  // { id: "users", label: "User Management", icon: "👥" },
  { id: "sdk", label: "SDK", icon: "🧰" },
  { id: "docs", label: "Documentation", icon: "📚" },
  { id: "contact", label: "Contact Us", icon: "✉️" },
];

const statsRows = [
  { offer: "19603849 - World Of Warships", app: "15935", clicks: 1, conv: 3, cr: "300%", payout: "$11.27", epc: "$11.27" },
  { offer: "19620723 - Alibaba", app: "15953", clicks: 1, conv: 0, cr: "0%", payout: "$0", epc: "$0" },
  { offer: "19622010 - Test Your Luck", app: "9523", clicks: 5, conv: 0, cr: "0%", payout: "$0", epc: "$0" },
  { offer: "19630891 - Movie Research", app: "9523", clicks: 1, conv: 0, cr: "0%", payout: "$0", epc: "$0" },
  { offer: "19631348 - Finance Research", app: "16504", clicks: 4, conv: 0, cr: "0%", payout: "$0", epc: "$0" },
  { offer: "19637892 - Blackjack 21: Blackjackist", app: "16502", clicks: 1, conv: 0, cr: "0%", payout: "$0", epc: "$0" },
  { offer: "19622011 - Survey Masters", app: "12002", clicks: 7, conv: 2, cr: "29%", payout: "$5.10", epc: "$0.73" },
  { offer: "19622012 - Crypto Quiz", app: "12003", clicks: 3, conv: 1, cr: "33%", payout: "$2.90", epc: "$0.97" },
];

const galleryApps = [
  "16528 - sumatrasIslandGold",
  "16527 - sleepTimeTracker",
  "16512 - emperorQuest",
  "16506 - premiumScratchCard",
  "16505 - mythicRealm",
  "16504 - dolphinOfBauhinia",
];

const placementsRowsDummy = [

];

const docsTopics = [
  "Welcome",
  "Create an app placement",
  "Integrate your app placement",
  "Postback notifications",
  "Offer notification service",
  "Android SDK",
];

export default function DesktopDashboard({ user, onUpdate }) {
  const [activeTab, setActiveTab] = useState("stats");
  const [search, setSearch] = useState("");
  const [billingSearch, setBillingSearch] = useState("");
  const [filterBy, setFilterBy] = useState("No Filter");
  const [groupBy, setGroupBy] = useState("Offer");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedGalleryApp, setSelectedGalleryApp] = useState(galleryApps[0]);
  const [selectedSdkApp, setSelectedSdkApp] = useState("");
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [activeDoc, setActiveDoc] = useState(docsTopics[0]);
  const [showAddApp, setShowAddApp] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [apps, setApps] = useState([]);
  const [appsLoading, setAppsLoading] = useState(false);
  const [withdrawOptions, setWithdrawOptions] = useState([]);
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [withdrawHistory, setWithdrawHistory] = useState([]);
  const [withdrawTotals, setWithdrawTotals] = useState({ amounts: {}, counts: {} });
  const [withdrawHistoryLoading, setWithdrawHistoryLoading] = useState(false);
  const [showDateRange, setShowDateRange] = useState(false);
  const dateRangeRef = useRef(null);
  const dateFromRef = useRef(null);
  const dateToRef = useRef(null);
  const [statsData, setStatsData] = useState(statsRows);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsSort, setStatsSort] = useState({ key: null, dir: null });
  const [contactValues, setContactValues] = useState({
    subject: "",
    appId: "general",
    message: "",
  });
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactStatus, setContactStatus] = useState("");
  const [contactError, setContactError] = useState("");
  const [statusUpdating, setStatusUpdating] = useState(new Set());

  const selectedSdkAppData = useMemo(() => {
    if (!selectedSdkApp) return null;
    return apps.find((app) => {
      const value = app._id || app.bundleId || app.name;
      return value === selectedSdkApp;
    }) || null;
  }, [selectedSdkApp, apps]);

  const parseNumber = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return value;
    const str = String(value).replace(/[$,%s]/g, '');
    const num = Number(str);
    return Number.isNaN(num) ? 0 : num;
  };

  const toggleStatsSort = (key) => {
    setStatsSort((prev) => {
      if (prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      if (prev.dir === 'desc') return { key: null, dir: null };
      return { key, dir: 'asc' };
    });
  };

  const filteredStats = useMemo(() => {
    if (!search) return statsData;
    const term = search.toLowerCase();
    return statsData.filter(
      (row) =>
        row.offer.toLowerCase().includes(term)
    );
  }, [search, statsData]);

  const sortedStats = useMemo(() => {
    if (!statsSort.key || !statsSort.dir) return filteredStats;
    const dir = statsSort.dir === 'asc' ? 1 : -1;
    const key = statsSort.key;
    return [...filteredStats].sort((a, b) => {
      const av = a[key];
      const bv = b[key];
      if (typeof av === 'string' || typeof bv === 'string') {
        if (key === 'offer' || key === 'app') {
          return String(av || '').localeCompare(String(bv || '')) * dir;
        }
      }
      return (parseNumber(av) - parseNumber(bv)) * dir;
    });
  }, [filteredStats, statsSort]);

  const groupByLabel = useMemo(() => {
    const val = String(groupBy || 'Offer');
    switch (val.toLowerCase()) {
      case 'app':
        return 'App';
      case 'date':
        return 'Date';
      case 'country':
        return 'Country';
      case 'user':
        return 'User';
      case 'offer':
      default:
        return 'Offer';
    }
  }, [groupBy]);

  const formatMoney = (value) => {
    const num = Number(value || 0);
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  const rangeLabel = dateFrom && dateTo
    ? `${dateFrom} - ${dateTo}`
    : dateFrom
      ? `${dateFrom} -`
      : dateTo
        ? `- ${dateTo}`
        : "";

  const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };

  const formatDateShort = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const accountInfoRows = useMemo(() => {
    const fallback = '—';
    return [
      { label: 'Pub ID', value: user?._id || user?.id || fallback },
      { label: 'Account Code', value: user?.apiKey || fallback },
      { label: 'Name', value: user?.contactPerson || fallback },
      { label: 'Email Address', value: user?.email || fallback },
      { label: 'Notifications Email Address', value: user?.email || fallback },
      { label: 'Company', value: user?.companyName || fallback },
      { label: 'Address', value: user?.address || fallback },
      { label: 'City', value: user?.city || fallback },
      { label: 'Country', value: user?.country || fallback },
      { label: 'Date Registered', value: formatDateShort(user?.createdAt) },
      { label: 'Privacy system integration key', value: user?.apiKey || fallback }
    ];
  }, [user]);
  const formatPercent = (value) => {
    if (value === undefined || value === null || value === "") return "0%";
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) return "0%";
      if (trimmed.endsWith("%")) return trimmed;
      const num = Number(trimmed);
      if (!Number.isNaN(num)) {
        const shown = Number.isInteger(num) ? num : Number(num.toFixed(2));
        return `${shown}%`;
      }
      return trimmed;
    }
    const num = Number(value);
    if (Number.isNaN(num)) return "0%";
    const shown = Number.isInteger(num) ? num : Number(num.toFixed(2));
    return `${shown}%`;
  };

  const formatPeriod = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    const start = new Date(date.getFullYear(), date.getMonth(), 1);
    const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    const startText = start.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
    const endText = end.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
    return `${startText} - ${endText}`;
  };

  const loadApps = useCallback(async () => {
    try {
      setAppsLoading(true);
      const res = await appsApi.listApps();
      const list = res?.result?.apps || res?.apps || [];
      setApps(list);
    } catch (err) {
      console.warn("Failed to load apps", err);
    } finally {
      setAppsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?._id || user?.id) {
      loadApps();
    }
  }, [user, loadApps]);

  const loadWithdrawOptions = useCallback(async () => {
    try {
      setWithdrawLoading(true);
      const res = await withdrawApi.getWithdrawOptions();
      const list = res?.result || res || [];
      setWithdrawOptions(list);
    } catch (err) {
      console.warn("Failed to load withdraw options", err);
      setWithdrawOptions([]);
    } finally {
      setWithdrawLoading(false);
    }
  }, []);

  const loadWithdrawHistory = useCallback(async () => {
    try {
      setWithdrawHistoryLoading(true);
      const res = await withdrawApi.getWithdraws();
      const result = res?.result || res || {};
      setWithdrawHistory(Array.isArray(result.items) ? result.items : []);
      setWithdrawTotals(result.totals || { amounts: {}, counts: {} });
    } catch (err) {
      console.warn("Failed to load withdraw history", err);
      setWithdrawHistory([]);
      setWithdrawTotals({ amounts: {}, counts: {} });
    } finally {
      setWithdrawHistoryLoading(false);
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    try {
      const res = await authApi.getProfile();
      const nextUser = res?.result?.user || res?.user;
      if (nextUser && onUpdate) {
        onUpdate(nextUser);
        if (typeof window !== "undefined") {
          window.localStorage.setItem("auth_user", JSON.stringify(nextUser));
        }
      }
    } catch (_) {
      // ignore profile refresh errors
    }
  }, [onUpdate]);

  const toggleAppStatus = useCallback(async (row) => {
    const id = row._id || row.bundleId || row.id;
    if (!id) return;
    const current = String(row.status || '').toLowerCase();
    const nextStatus = current === 'active' ? 'inactive' : 'active';
    setStatusUpdating((prev) => new Set(prev).add(id));
    try {
      await appsApi.updateAppStatus({ appId: id, bundleId: row.bundleId, status: nextStatus });
      await loadApps();
    } catch (err) {
      console.warn('Failed to update app status', err);
    } finally {
      setStatusUpdating((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }, [loadApps]);

const filteredWithdraws = useMemo(() => {
    if (!billingSearch) return withdrawHistory;
    const term = billingSearch.toLowerCase();
    return withdrawHistory.filter((item) => {
      const status = (item.status || "").toLowerCase();
      const amount = String(item.amount || "");
      const dateText = formatDate(item.createdAt).toLowerCase();
      const periodText = formatPeriod(item.createdAt).toLowerCase();
      return status.includes(term) || amount.includes(term) || dateText.includes(term) || periodText.includes(term);
    });
  }, [billingSearch, withdrawHistory]);

  useEffect(() => {
    if (!selectedSdkApp && apps.length > 0) {
      const first = apps[0];
      setSelectedSdkApp(first._id || first.bundleId || first.name || "");
    }
  }, [apps, selectedSdkApp]);

  useEffect(() => {
    if (activeTab === "billing") {
      loadWithdrawOptions();
    }
  }, [activeTab, loadWithdrawOptions]);

  useEffect(() => {
    if (activeTab === "billing") {
      loadWithdrawHistory();
    }
  }, [activeTab, loadWithdrawHistory]);

  const loadStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const payload = {};
      if (dateFrom) payload.fromDate = new Date(dateFrom).toISOString();
      if (dateTo) payload.toDate = new Date(dateTo).toISOString();
      if (groupBy) payload.groupBy = String(groupBy).toLowerCase();
      const res = await statsApi.getSiteUserStats(payload);
      const result = res?.result || res?.data || {};
      const list = Array.isArray(result) ? result : (result?.offers || []);
      const normalized = Array.isArray(list)
        ? list.map((item) => ({
            offer: item.groupLabel || item.offer || item.offerName || item.groupKey || "N/A",
            app: item.app || item.appName || "",
            clicks: item.clicks || item.click || item.impressions || 0,
            conv: item.conv || item.conversions || 0,
            cr: formatPercent(item.cr !== undefined && item.cr !== null ? item.cr : (item.clicks ? (item.conv || 0) / item.clicks * 100 : 0)),
            payout: item.payout ? `$${item.payout}` : "$0",
            epc: item.epc ? `$${item.epc}` : "$0"
          }))
        : [];
      if (normalized.length) {
        setStatsData(normalized);
      } else {
        setStatsData([]);
      }
    } catch (err) {
      console.warn("Failed to load stats", err);
      setStatsData([]);
    } finally {
      setStatsLoading(false);
    }
  }, [dateFrom, dateTo, groupBy]);

  const contactAppOptions = useMemo(() => {
    const list = Array.isArray(apps) ? apps : [];
    return [
      { value: "general", label: "General (not related to an app)" },
      ...list.map((app) => {
        const value = app._id || app.bundleId || app.name;
        const label = app.name || app.bundleId || value;
        return { value, label };
      }),
    ];
  }, [apps]);

  const handleContactChange = (event) => {
    const { name, value } = event.target;
    setContactValues((prev) => ({ ...prev, [name]: value }));
    if (contactStatus) setContactStatus("");
    if (contactError) setContactError("");
  };

  const handleContactSubmit = async (event) => {
    event.preventDefault();
    if (contactSubmitting) return;
    setContactSubmitting(true);
    setContactStatus("");
    setContactError("");
    try {
      const selectedApp = apps.find((app) => {
        const value = app._id || app.bundleId || app.name;
        return value === contactValues.appId;
      });
      await contactApi.submitContact({
        subject: contactValues.subject,
        appId: contactValues.appId,
        appName: selectedApp?.name || selectedApp?.bundleId || "",
        message: contactValues.message,
      });
      setContactStatus("success");
      setContactValues({ subject: "", appId: "general", message: "" });
    } catch (err) {
      const message = err?.data?.statusText || err?.message || "Failed to submit contact request.";
      setContactError(message);
    } finally {
      setContactSubmitting(false);
    }
  };

  useEffect(() => {
    if (activeTab === "stats") {
      loadStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (!showDateRange) return;
    const handleClick = (event) => {
      if (dateRangeRef.current && !dateRangeRef.current.contains(event.target)) {
        setShowDateRange(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showDateRange]);

  return (
    <div className="dashboard-shell">
      <aside className="dashboard-sidebar">
        <div className="sidebar-brand">
          <div className="brand-mark">GW</div>
          <div className="brand-name">Givvy Metrics</div>
        </div>
        <div className="sidebar-user">
          <p className="user-welcome">Welcome,</p>
          <p className="user-name">Publisher</p>
          <p className="user-id">Pub ID: {user ? (user._id || user.id || '-') : '-'}</p>
          <p className="user-balance">Balance: {user ? formatMoney(user.balance ?? 0) : "-"}</p>
        </div>
        <div className="sidebar-account">
          <button
            type="button"
            className="account-toggle"
            onClick={() => setActiveTab('account')}
          >
            Account Info
          </button>
        </div>
        <nav className="sidebar-nav">
          {navItems.map((item) => (
            <div
              key={item.id}
              className={`nav-item ${activeTab === item.id ? "is-active" : ""}`}
              onClick={() => setActiveTab(item.id)}
            >
              <span className="nav-icon">{item.icon}</span>
              {item.label}
            </div>
          ))}
        </nav>
        <div className="sidebar-help">
          <p className="help-title">Need help?</p>
          <p className="help-text">Contact your Account Manager.</p>
          <p className="help-email">support@givvyoffers.com</p>
        </div>
      </aside>

      <main className="dashboard-main">
        {activeTab === "stats" && (
          <>
            <header className="stats-header">
              <div>
                <p className="stats-eyebrow">Statistics</p>
                <h1 className="stats-title">Show Statistics</h1>
              </div>
        </header>

            <section className="stats-card">
              <div className="filters-row">
                <div className="filter-field date-range" ref={dateRangeRef}>
                  <label>Date Range</label>
                  <input
                    type="text"
                    readOnly
                    placeholder="Select date range"
                    value={rangeLabel}
                    onClick={() => {
                      setShowDateRange(true);
                      setTimeout(() => {
                        if (dateFromRef.current?.showPicker) {
                          dateFromRef.current.showPicker();
                        } else {
                          dateFromRef.current?.focus();
                        }
                      }, 0);
                    }}
                  />
                  {showDateRange && (
                    <div className="date-range-popover">
                      <div className="date-range-row">
                        <label>From</label>
                        <input
                          ref={dateFromRef}
                          type="date"
                          value={dateFrom}
                          onChange={(e) => {
                            const nextFrom = e.target.value;
                            setDateFrom(nextFrom);
                            if (dateTo && nextFrom && nextFrom > dateTo) {
                              setDateTo("");
                            }
                            setTimeout(() => {
                              if (dateToRef.current?.showPicker) {
                                dateToRef.current.showPicker();
                              } else {
                                dateToRef.current?.focus();
                              }
                            }, 0);
                          }}
                        />
                      </div>
                      <div className="date-range-row">
                        <label>To</label>
                        <input
                          ref={dateToRef}
                          type="date"
                          value={dateTo}
                          onChange={(e) => {
                            const nextTo = e.target.value;
                            setDateTo(nextTo);
                            if (dateFrom && nextTo && nextTo < dateFrom) {
                              setDateFrom("");
                            }
                            if (nextTo) setShowDateRange(false);
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div className="filter-field">
                  <label>Group By</label>
                  <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
                    {['Offer','App','Date','Country','User'].map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* <div className="filters-row">
                <div className="filter-field">
                  <label>Group By</label>
                  <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
                    {["Offer", "App", "Country"].map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                <div className="filter-field">
                  <label>Filter By</label>
                  <select value={filterBy} onChange={(e) => setFilterBy(e.target.value)}>
                    {["No Filter", "Top EPC", "Top CR", "High Payout"].map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
              </div> */}

              <div className="actions-row">
                <button className="btn process" onClick={loadStats} disabled={statsLoading}>
                  {statsLoading ? "Loading..." : "▾ Process"}
                </button>
                {/* <button className="btn csv">⇩ CSV</button> */}
              </div>
            </section>

            <section className="table-card">
              <div className="table-head">
                <h3>Offer Performance</h3>
                <div className="table-search">
                  <label>Search:</label>
                  <input
                    type="text"
                    placeholder="Search offers or apps"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th><span className="th-label">{groupByLabel}</span><button type="button" className="sort-btn" onClick={() => toggleStatsSort('offer')} aria-label="Sort Offer"><span className={"sort-caret up" + (statsSort.key === 'offer' && statsSort.dir === 'asc' ? ' is-active' : '')} /> <span className={"sort-caret down" + (statsSort.key === 'offer' && statsSort.dir === 'desc' ? ' is-active' : '')} /></button></th>
                      <th><span className="th-label">App</span><button type="button" className="sort-btn" onClick={() => toggleStatsSort('app')} aria-label="Sort App"><span className={"sort-caret up" + (statsSort.key === 'app' && statsSort.dir === 'asc' ? ' is-active' : '')} /> <span className={"sort-caret down" + (statsSort.key === 'app' && statsSort.dir === 'desc' ? ' is-active' : '')} /></button></th>
                      <th><span className="th-label">Clicks</span><button type="button" className="sort-btn" onClick={() => toggleStatsSort('clicks')} aria-label="Sort Clicks"><span className={"sort-caret up" + (statsSort.key === 'clicks' && statsSort.dir === 'asc' ? ' is-active' : '')} /> <span className={"sort-caret down" + (statsSort.key === 'clicks' && statsSort.dir === 'desc' ? ' is-active' : '')} /></button></th>
                      <th><span className="th-label">Conversions</span><button type="button" className="sort-btn" onClick={() => toggleStatsSort('conv')} aria-label="Sort Conversions"><span className={"sort-caret up" + (statsSort.key === 'conv' && statsSort.dir === 'asc' ? ' is-active' : '')} /> <span className={"sort-caret down" + (statsSort.key === 'conv' && statsSort.dir === 'desc' ? ' is-active' : '')} /></button></th>
                      <th><span className="th-label">CR</span><button type="button" className="sort-btn" onClick={() => toggleStatsSort('cr')} aria-label="Sort CR"><span className={"sort-caret up" + (statsSort.key === 'cr' && statsSort.dir === 'asc' ? ' is-active' : '')} /> <span className={"sort-caret down" + (statsSort.key === 'cr' && statsSort.dir === 'desc' ? ' is-active' : '')} /></button></th>
                      <th><span className="th-label">Payout</span><button type="button" className="sort-btn" onClick={() => toggleStatsSort('payout')} aria-label="Sort Payout"><span className={"sort-caret up" + (statsSort.key === 'payout' && statsSort.dir === 'asc' ? ' is-active' : '')} /> <span className={"sort-caret down" + (statsSort.key === 'payout' && statsSort.dir === 'desc' ? ' is-active' : '')} /></button></th>
                      <th><span className="th-label">EPC</span><button type="button" className="sort-btn" onClick={() => toggleStatsSort('epc')} aria-label="Sort EPC"><span className={"sort-caret up" + (statsSort.key === 'epc' && statsSort.dir === 'asc' ? ' is-active' : '')} /> <span className={"sort-caret down" + (statsSort.key === 'epc' && statsSort.dir === 'desc' ? ' is-active' : '')} /></button></th>
                    </tr>
                  </thead>
                  <tbody>
                    {statsLoading && (
                      <tr><td colSpan="7">Loading stats...</td></tr>
                    )}
                    {!statsLoading && filteredStats.length === 0 && (
                      <tr><td colSpan="7">No stats available.</td></tr>
                    )}
                    {!statsLoading && sortedStats.map((row, idx) => (
                      <tr key={idx}>
                        <td>{row.offer}</td>
                        <td>{row.app}</td>
                        <td>{row.clicks}</td>
                        <td>{row.conv}</td>
                        <td>{row.cr}</td>
                        <td>{row.payout}</td>
                        <td>{row.epc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}

        {activeTab === "billing" && (
          <>
            <header className="stats-header">
              <div>
                <p className="stats-eyebrow">Billing</p>
                <h1 className="stats-title">Overview</h1>
              </div>
              <div className="billing-actions">
                <button className="pill-btn" onClick={() => { setShowWithdraw((v) => !v); }}>↗ Withdraw</button>
                {/* <button className="pill-btn">💳 Payment Details</button> */}
              </div>
            </header>

            {showWithdraw && (
              <section className="stats-card" style={{ marginBottom: 18 }}>
                <WithdrawPanel
                  options={withdrawOptions}
                  loading={withdrawLoading}
                  onSubmit={async ({ amount, providerType, fields }) => {
                    try {
                      await withdrawApi.requestWithdraw({ amount, providerType, fields });
                      await Promise.all([loadWithdrawHistory(), loadWithdrawOptions(), refreshProfile()]);
                    } catch (err) {
                      const message = err?.data?.statusText || err?.message || "Withdraw failed";
                      throw new Error(message);
                    }
                  }}
                />
              </section>
            )}

            <section className="billing-cards">
              <div className="billing-card teal">
                <p>PENDING BILLS</p>
                <strong>{formatMoney(withdrawTotals?.amounts?.pending)}</strong>
              </div>
              <div className="billing-card lime">
                <p>TOTAL PAID</p>
                <strong>{formatMoney(withdrawTotals?.amounts?.completed)}</strong>
              </div>
            </section>

            <section className="table-card">
              <div className="table-head">
                <h3>Billing Information</h3>
                <div className="table-search">
                  <label>Search:</label>
                  <input
                    type="text"
                    placeholder="Search periods"
                    value={billingSearch}
                    onChange={(e) => setBillingSearch(e.target.value)}
                  />
                </div>
              </div>
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Billing Period</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Transaction Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {withdrawHistoryLoading && (
                      <tr>
                        <td colSpan="4">Loading withdrawals...</td>
                      </tr>
                    )}
                    {!withdrawHistoryLoading && filteredWithdraws.length === 0 && (
                      <tr>
                        <td colSpan="4">No withdrawals found.</td>
                      </tr>
                    )}
                    {!withdrawHistoryLoading && filteredWithdraws.map((row) => (
                      <tr key={row._id}>
                        <td>{formatPeriod(row.createdAt)}</td>
                        <td>{formatMoney(row.amount)}</td>
                        <td>{row.status || "pending"}</td>
                        <td>{formatDate(row.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}


        {/* Account pages */}
        {activeTab === "account" && <AccountInfo info={accountInfoRows} user={user} onUpdate={onUpdate} />}

        {activeTab === "offers" && (
          <>
            <header className="stats-header">
              <div>
                <p className="stats-eyebrow">Offers Gallery</p>
                <h1 className="stats-title">Choose App</h1>
              </div>
            </header>

            <section className="table-card">
              <div className="filters-row">
                <div className="filter-field">
                  <label>Select App</label>
                  <select
                    value={selectedGalleryApp}
                    onChange={(e) => setSelectedGalleryApp(e.target.value)}
                  >
                    {galleryApps.map((app) => (
                      <option key={app} value={app}>{app}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="actions-row">
                <button className="btn csv" style={{ background: "#ffb300", color: "#1f2450" }}>
                  ▾ Get Offers
                </button>
              </div>
              <div className="placeholder-box">
                <p>Select an app and click Get Offers to preview creatives.</p>
              </div>
            </section>
          </>
        )}

        {activeTab === "placements" && (
          <>
            <header className="stats-header">
              <div>
                <p className="stats-eyebrow">App Placements</p>
                <h1 className="stats-title">Overview</h1>
              </div>
              <button
                className="pill-btn"
                onClick={() => {
                  setShowAddApp((prev) => !prev);
                  if (showAddApp) {
                    setEditingApp(null);
                  }
                }}
              >
                {showAddApp ? "Close" : "＋ Add App"}
              </button>
            </header>

            {showAddApp ? (
              <AddAppWizard
                initialApp={editingApp}
                onClose={() => {
                  setShowAddApp(false);
                  setEditingApp(null);
                  loadApps();
                }}
              />
            ) : (
              <section className="table-card">
                <div className="table-head">
                  <h3>Overview</h3>
                  <div className="table-search">
                    <label>Search:</label>
                    <input type="text" placeholder="Search apps" />
                  </div>
                </div>
                <div className="table-wrapper">
                  <table>
                    <thead>
                      <tr>
                        <th>App ID</th>
                        <th>Name</th>
                        <th>Status</th>
                        <th>Monetization Tool</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(apps.length ? apps : placementsRowsDummy).map((row) => (
                        <tr key={row._id || row.id}>
                          <td>{row.bundleId || row.id}</td>
                        <td>{row.name}</td>
                        <td><span className={`status-dot status-${(row.status || "inactive").toLowerCase()}`} /> {(row.status || "inactive").toLowerCase()}</td>
                        <td>{row.monetizationTool || row.tool || "Offerwall SDK - Android"}</td>
                        <td className="actions-cell">
                          <button
                            type="button"
                            className="link-btn"
                            onClick={() => {
                              setEditingApp(row);
                              setShowAddApp(true);
                            }}
                          >
                            ✏️ Edit
                          </button>
                          <button
                            type="button"
                            className="link-btn"
                            onClick={() => toggleAppStatus(row)}
                            disabled={statusUpdating.has(row._id || row.bundleId || row.id)}
                          >
                            {(row.status || '').toLowerCase() === 'active' ? '⏸ Stop' : '▶ Start'}
                          </button>
                        </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                {appsLoading && <div className="table-loading">Loading apps...</div>}
                {!appsLoading && apps.length === 0 && (
                  <div className="table-loading">No apps added yet.</div>
                )}
                </div>
              </section>
            )}
          </>
        )}

        {activeTab === "users" && (
          <>
            <header className="stats-header">
              <div>
                <p className="stats-eyebrow">User Management</p>
                <h1 className="stats-title">Manage Team</h1>
              </div>
            </header>
            <section className="table-card">
              <div className="placeholder-box">
                <p>Invite team members, set permissions, and manage access. (Dummy content)</p>
              </div>
            </section>
          </>
        )}

        {activeTab === "sdk" && (
          <>
            <header className="stats-header">
              <div>
                <p className="stats-eyebrow">SDK</p>
                <h1 className="stats-title">Download App SDK</h1>
              </div>
            </header>
            <section className="table-card">
              <div className="filter-field" style={{ maxWidth: 420 }}>
                <label>Select App</label>
                <select
                  value={selectedSdkApp}
                  onChange={(e) => setSelectedSdkApp(e.target.value)}
                  disabled={apps.length === 0}
                >
                  {apps.length === 0 && <option value="">No apps available</option>}
                  {apps.map((app) => {
                    const value = app._id || app.bundleId || app.name;
                    const label = app.name || app.bundleId || value;
                    return (
                      <option key={value} value={value}>{label}</option>
                    );
                  })}
                </select>
              </div>
              <div className="placeholder-box">
                {apps.length === 0 ? (
                  <>
                    <p><strong>No apps added yet.</strong></p>
                    <p>Add an app in App Placements to download SDKs.</p>
                  </>
                ) : selectedSdkAppData ? (
                  <>
                    <p><strong>Selected App:</strong> {selectedSdkAppData.name || selectedSdkAppData.bundleId || selectedSdkApp}</p>
                    <p><strong>Package Name:</strong> {selectedSdkAppData.bundleId || selectedSdkAppData.name || selectedSdkApp}</p>
                  </>
                ) : (
                  <>
                    <p><strong>No App Selected.</strong></p>
                    <p>Select the appropriate app to download the corresponding SDK tool.</p>
                  </>
                )}
              </div>
            </section>
          </>
        )}

        {activeTab === "docs" && (
          <>
            <header className="stats-header">
              <div>
                <p className="stats-eyebrow">Documentation</p>
                <h1 className="stats-title">{activeDoc}</h1>
              </div>
            </header>
            <section className="docs-layout">
              <div className="docs-nav">
                {docsTopics.map((topic) => (
                  <div
                    key={topic}
                    className={`docs-item ${topic === activeDoc ? "is-active" : ""}`}
                    onClick={() => setActiveDoc(topic)}
                  >
                    {topic}
                  </div>
                ))}
              </div>
              <div className="docs-content">
                {activeDoc === "Welcome" ? (
                  <div className="docs-article">
                    <h2 className="docs-title">Welcome</h2>
                    <p className="docs-lead">Here you will find all the info you need to integrate the various monetization tools Torox has to offer</p>
                    <h3 className="docs-section-title">Publisher account creation</h3>
                    <ol className="docs-steps">
                      <li>You need to create a Publisher Account to start using our services. The registration process is very easy, just complete the required form fields.</li>
                      <li>Once you create your account, expect to receive a confirmation email. Click on the confirmation link in order to complete the registration process.</li>
                      <li>After you confirm your E-mail, you can login.</li>
                      <li>When you login you will be presented with the home page, which is presenting empty statistics because you don’t have any activity yet.</li>
                      <li>At this point, your account is being reviewed. You can create apps and update your payment information while your account is pending but you’ll not be able to see offers. Account activation process may take up to 48 hours.</li>
                    </ol>
                  </div>
                ) : activeDoc === "Create an app placement" ? (
                  <div className="docs-article">
                    <h2 className="docs-title">Create an app placement</h2>
                    <p className="docs-lead">To create your first App Placement click on the Add App Placement section</p>
                    <h3 className="docs-section-title">Note:</h3>
                    <p>If you would like to add more than one App, please notice each App can be used for a specific platform/monetization tool. Please create one App per platform (web or mobile) and choose the monetization tool accordingly.</p>
                    <h3 className="docs-section-title">Add app placement form:</h3>
                    <ul className="docs-bullets">
                      <li><strong>App Name:</strong> The name of your app/game/website, give it a meaningful name.</li>
                      <li><strong>App URL:</strong> The URL of your website or app</li>
                    </ul>
                  </div>
                ) : activeDoc === "Integrate your app placement" ? (
                  <div className="docs-article">
                    <h2 className="docs-title">Integrate your App Placement (No Longer Available for New Placements)</h2>
                    <p className="docs-lead">To integrate app, select App in the Overview section and press Edit</p>
                  </div>
                ) : activeDoc === "New Wall Integration" ? (
                  <div className="docs-article">
                    <h2 className="docs-title">New Wall Integration</h2>
                    <p className="docs-lead">Our new version of wall includes an S2S-authenticated integration that unlocks a faster, more secure, and more feature-rich experience.</p>

                    <p>Moving from client-side to server-side not only strengthens security—it also gives you access to capabilities that are no longer supported in the legacy version:</p>
                    <ul className="docs-bullets">
                      <li>1-second load times (down from 9-10 seconds)</li>
                      <li>Enhanced data signals for deeper user insights and future personalization</li>
                      <li>Significantly improved UX, including:
                        <ul className="docs-bullets docs-bullets--nested">
                          <li>Higher-quality creatives</li>
                          <li>Smart filtering between games and tasks</li>
                          <li>Clearer visibility into offer progress</li>
                        </ul>
                      </li>
                    </ul>

                    <p>With the new wall the URL is no longer exposing data/info to anyone (only a random token), It is generated only on your request to our system</p>

                    <h3 className="docs-section-title">There are a lot of pros for moving forward to this system:</h3>
                    <ul className="docs-bullets">
                      <li>You are the only one that can make token for players.</li>
                      <li>No more ghost users in your wall.</li>
                      <li>Malicious users/actors would not be able to see and play with other players wall (tokens are random).</li>
                      <li>In the future we might add expiration to the tokens, this will lead users back to your site to get new token.</li>
                    </ul>

                    <p>To help you get started, the section below outlines the technical implementation steps for the new S2S integration.</p>

                    <h3 className="docs-section-title">Prerequisites</h3>
                    <ul className="docs-bullets">
                      <li>Placement ID - app_id</li>
                      <li>Token (your app key - can be obtained from app settings in publisher dashboard)</li>
                      <li>Our API URL - https://api.wall.torox.io</li>
                      <li>Your user details</li>
                    </ul>

                    <h3 className="docs-section-title">API Authentication</h3>

                    <h3 className="docs-section-title">Request</h3>
                    <pre className="docs-code"><code>{"POST https://api.wall.torox.io/partner/session?placement_id=<YOUR_PLACEMENT_ID>&token=<YOUR_APP_TOKEN>\n{\n  \"player\": {\n    \"uid\": \"<YOUR_USER_ID>\"\n  }\n}"}</code></pre>

                    <p><strong>* DO NOT SHARE YOUR APP_TOKEN IN ANY CLIENT! THIS OPERATION SHOULD BE DONE BETWEEEN YOUR SERVER AND OUR SERVER</strong></p>

                    <h3 className="docs-section-title">Response</h3>
                    <pre className="docs-code"><code>{"{\n  \"ok\": true,\n  \"wall_url\": \"https://wall.torox.io/<PLAYER_TOKEN>\"\n}"}</code></pre>

                    <p><strong>Note:</strong> The generated URL can be provided to the user and placed in an iframe directly.</p>
                  </div>
                ) : activeDoc === "App Promos" ? (
                  <div className="docs-article">
                    <h2 className="docs-title">App Promos</h2>
                    <p className="docs-lead">App Promo feature lets you increase your app currency presented to your users!</p>
                    <p>This modification can be done for a limited known-ahead time and lets you choose targeted countries as well. Applying this changes over API offers can also be modified by you</p>
                    <p>To access App Promos, select the App in the Overview section and press Edit</p>
                    <p><strong>Go to the App Promos section</strong></p>

                    <h3 className="docs-section-title">Explanation of fields - App Promos:</h3>
                    <ul className="docs-bullets">
                      <li><strong>Activate Increased Currency Promo:</strong> Check this if you wish to run the Promo at the specified date, time and duration. During the duration of the Promo, the currency amount given to your users will be increased.</li>
                      <li><strong>% Increase:</strong> The percentage of currency increase for the duration. The minimum increase is 0%, with 150% as the maximum. If you need to set a higher percentage, please contact your assigned Account Manager.</li>
                      <li><strong>No. of Hours:</strong> Specifies the duration of the Promo in hours (HH:mm format). For example, if you intend to run the Promo for 6 hours you can simply write 6. If you need to run the promo for 24 hours and 30 minutes, simply write 24:30.</li>
                      <li><strong>Start Date/Time:</strong> Specifies the starting date and time of the Promo. Click anywhere in the textbox to select the date or time. Date and Time should be in EST timezone.</li>
                      <li><strong>Allow in API/SDK Offers:</strong> Select this option to apply the Promo to API offers.</li>
                      <li><strong>Apply To Specific Countries Only?:</strong> Select this option to apply the Promo to specific countries only. Once selected, a textbox will appear for you to select the countries. You can add, or remove, multiple countries as you like.</li>
                    </ul>

                    <p>After filling-up all the information, Click Save. Clicking Clear will disable and clear all fields in the current session. Click Save to persist the data.</p>
                  </div>
                ) : activeDoc === "Offer Promos" ? (
                  <div className="docs-article">
                    <h2 className="docs-title">Offer Promos</h2>
                    <p className="docs-lead">This function allows you to promote any offer on the wall, by either adding a visual badge to the offer, increasing the reward, or both.</p>
                    <p>This modification can be set for a limited time period and lets you choose targeted countries as well. You can select to apply these changes over API.</p>
                    <p>To access Offer Promos, go to the Offers Gallery and select the relevant app. Click on the offer you wish to promote, This will open the offer page, as shown in the screenshot below.</p>

                    <h3 className="docs-section-title">To set an offer promo:</h3>
                    <ol className="docs-steps">
                      <li>Click on the "Change" button. This will open a form for the settings.</li>
                      <li>Supply all the needed fields, as explained below.</li>
                      <li>Click save once done.</li>
                    </ol>

                    <h3 className="docs-section-title">VC Settings:</h3>
                    <ul className="docs-bullets">
                      <li><strong>Old VC</strong> The Old Virtual Currency value that will be shown to the user.</li>
                      <li><strong>New VC</strong> The New Virtual Currency value that will be shown to the user. You can choose how much to reward your users for converting the offer. You can set this to be the default value and lower the Old VC, to make this offer stand out.</li>
                    </ul>

                    <h3 className="docs-section-title">Time Settings:</h3>
                    <ul className="docs-bullets">
                      <li><strong>Start Date/Time</strong> Specifies the starting date and time of the Promo. Click anywhere in the textbox to select the date or time. Date and Time should be in EST timezone.</li>
                      <li><strong>End Date/Time</strong> Specifies the ending date and time of the Promo. Click anywhere in the textbox to select the date or time. Date and Time should be in EST timezone.</li>
                    </ul>

                    <h3 className="docs-section-title">API & Wall Settings:</h3>
                    <ul className="docs-bullets">
                      <li><strong>Allow in API</strong> By default, API does not allow offer promotion. Check this box if you want the API to use promotion.</li>
                      <li><strong>Present in Wall</strong> Check this box if you want to show the crossed out old VC value and a "Promoted Offer" badge to make it stand out.</li>
                    </ul>
                  </div>
                ) : activeDoc === "Postback notifications" ? (
                  <div className="docs-article">
                    <h2 className="docs-title">Postback Notifications</h2>
                    <p className="docs-lead">Whenever a user makes a conversion, we will send you a URL request, called a 'Server to Server Postback' with some information. Using this information, you can reward the user who performed the action accordingly.</p>
                    <p>The postback will be sent to the global postback URL you have defined in your Torox's App Placement section.</p>
                    <p>The information will be sent to your postback URL, in one of the 2 methods:</p>

                    <h3 className="docs-section-title">Minimal Parameter Postback:</h3>
                    <p>If you are using an Iframe based OfferWall, this method is recommended. In Minimal Parameter Postback, the postback data added after the end of your postback url such as : http://www.Your-Site.com/pb/?OID=123&user_id=JohnSmith&Amount=10...</p>

                    <h3 className="docs-section-title">Custom Parameter Postback:</h3>
                    <p>In this method, Postback Data is added to your own parameters such as: http://www.Your-Site.com/pb/?YourParmater1={'{id}'}&YourParamater2={'{oid}'}&YourClickId=user_id etc...</p>
                    <p>If you are using our API / Stand-Alone offers, this method is recommended.</p>
                    <p>Our main clickId parameter is called user_id. You can also pass/receive additional subids, using the following subid parameters:</p>
                    <p>Subid1, subid2, subid3. Please check our API doc page for more info.</p>

                    <h3 className="docs-section-title">Note:</h3>
                    <p>It is highly recommended to add oid parameter to your app postback url. This parameter represents the id of the converted offer. Comparing the id of the offer the user clicked on to the id of the offer which was converted can help you detect and avoid fraud attempts on your system.</p>

                    <h3 className="docs-section-title">Parameters List</h3>
                    <p>The following is a list of all the parameters which are passed to you for every postback:</p>
                    <ul className="docs-bullets">
                      <li><strong>id</strong> = The ID of the conversion in our system</li>
                      <li><strong>oid</strong> = The ID of the offer converted in our system</li>
                      <li><strong>amount</strong> = The amount that your user should receive for this conversion nominated in your currency. For example if your currency name is Points and your Currency Equivalent field is 35 and the conversion is worth $1, we will send "35" in this field.</li>
                      <li><strong>currency_name</strong> = The currency name that you set in your App's Placement page.</li>
                      <li><strong>user_id</strong> = The id of your user, the one you have placed in the iframe integration. This id should not be empty or 0.</li>
                      <li><strong>payout</strong> = The publisher's revenue for this conversion.</li>
                      <li><strong>o_name</strong> = The name of the offer converted.</li>
                      <li><strong>sig</strong> = A signature to be sure the postback is legit. The signature is composed the following way: md5(oid + "-" + user_id + "-" + Your App Placement's KEY). You can find your App's Placement key in the App's details page. AKA secret key.</li>
                      <li><strong>package_id (for mobile campaigns only)</strong> = The package id for the android / iTunes app.</li>
                      <li><strong>ip_address</strong> = IP Address of the user when the click happened.</li>
                      <li><strong>event</strong> = the name of the event the user completed. This will be added if the offer has multiple events</li>
                      <li><strong>subid1 / subid2 / subid3</strong> = Those would be passed in case you have sent us some subid data (optional)</li>
                      <li><strong>click_ts</strong> = The unix timestamp when the user clicked on the offer.</li>
                      <li><strong>conversion_ts</strong> = The unix timestamp when the actual conversion happen.</li>
                    </ul>

                    <p>If you would like to pass those additional subids in your iframe based OfferWall, they can be passed in the following way.</p>
                    <p>https://torox.io/ifr/show/YOUR_PUB_ID/UserID/YOUR_APP_ID/0/0/subid1/subid2/subid3.</p>

                    <h3 className="docs-section-title">Test Postback</h3>
                    <p>In order to test the postback URL you set you can go to App placement, choose the app you would like to test and click on Postback Tester panel</p>

                    <h3 className="docs-section-title">Important:</h3>
                    <p>We expect a "1" (without quotes) as a response to a successfully received postback and a "0" (without quotes) to indicate that the postback was received incorrectly. We will try to send the postback up to 5 times, based on the app settings, if we have not received a "1" as a reply. After that, we will send you an email with the data of the conversion so you can process it manually.</p>

                    <h3 className="docs-section-title">Postback IPs</h3>
                    <p>All of our postbacks shall be sent from the following Torox IP: 44.212.211.226 Please white-list this IP as this is the ONLY IP we'll be sending postbacks from.</p>

                    <h3 className="docs-section-title">Reversal Postbacks</h3>
                    <p>By default, all reversal postbacks are disabled. You can choose to enable it by going to the settings page on one of your app placements as shown below:</p>
                    <p><strong>enable reversal postbacks</strong></p>
                    <p>Check the box that says "Send Negative Postback in case of Reversals?"</p>
                    <p><strong>Important:</strong> All reversal postbacks will have a negative amount or payout.</p>
                  </div>
                ) : activeDoc === "Offer notification service" ? (
                  <div className="docs-article">
                    <h2 className="docs-title">Get Notification</h2>
                    <p className="docs-lead">In order to be notified about an offer changes you should subscribe to the notification service. Torox will send you a notification by email for the next events:</p>
                    <ul className="docs-bullets">
                      <li>Offer has been paused</li>
                      <li>Offer has been reactivated</li>
                      <li>Offer payout has been changed</li>
                      <li>Offer cap has been changed</li>
                    </ul>
                    <p>To subscribe for an offer notification enter the Offers Gallery and choose the app placement. After choosing the app placement a list of all available offers will be presented. Tick the Notify tick the box next to the offer you would like to subscribe and get notifications for or untick the box in order to remove the subscription.</p>
                    <p>Another possibility available is to tick Enable Notification tick box via offer view.</p>

                    <h3 className="docs-section-title">Notification Email Address</h3>
                    <p>Notifications will be sent to the email address you are logging into Torox system with by default. Changing the notification email address can be done under Account Info. Click on My Account then on Account Info. On Edit Account Information section enter the desired mail address for notification subscription to field Notifications Email Address.</p>
                  </div>
                ) : (
                  <p className="docs-lead">Content coming soon.</p>
                )}
              </div>
            </section>
          </>
        )}

        {activeTab === "contact" && (
          <>
            <header className="stats-header">
              <div>
                <p className="stats-eyebrow">Contact Us</p>
                <h1 className="stats-title">Report a Problem</h1>
              </div>
            </header>
            <section className="contact-card">
              <form className="contact-form" onSubmit={handleContactSubmit}>
                <label className="contact-field">
                  <span>Subject</span>
                  <input
                    type="text"
                    name="subject"
                    placeholder="Short subject"
                    value={contactValues.subject}
                    onChange={handleContactChange}
                    required
                  />
                </label>
                <label className="contact-field">
                  <span>App</span>
                  <select
                    name="appId"
                    value={contactValues.appId}
                    onChange={handleContactChange}
                    disabled={appsLoading}
                  >
                    {contactAppOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </label>
                <label className="contact-field">
                  <span>Problem description</span>
                  <textarea
                    name="message"
                    placeholder="Explain the issue..."
                    rows={6}
                    value={contactValues.message}
                    onChange={handleContactChange}
                    required
                  />
                </label>
                <div className="contact-actions">
                  <button type="submit" className="btn process" disabled={contactSubmitting}>
                    {contactSubmitting ? "Sending..." : "Submit"}
                  </button>
                </div>
                {contactStatus === "success" && (
                  <p className="contact-status success">Thanks! Your message has been sent.</p>
                )}
                {contactError && (
                  <p className="contact-status error">{contactError}</p>
                )}
              </form>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
